'use client'

import { useEffect } from 'react'
import { runCookieCleanup } from '@/lib/supabase/client'

/**
 * Hook that runs cookie cleanup on mount and periodically
 *
 * This helps prevent HTTP 431 "Request Header Fields Too Large" errors
 * caused by cookie accumulation from multiple Supabase projects on the same domain.
 *
 * How it works:
 * 1. On initial mount, runs cleanup to remove any accumulated foreign cookies
 * 2. Sets up a periodic interval (default: 5 minutes) to clean up any cookies
 *    that accumulate during the session
 *
 * The cleanup is lightweight - it only removes cookies from OTHER Supabase projects,
 * not the current project's cookies.
 *
 * @param intervalMs - How often to run cleanup (default: 5 minutes). Set to 0 to disable periodic cleanup.
 */
export function useCookieCleanup(intervalMs: number = 5 * 60 * 1000): void {
    useEffect(() => {
        // Run cleanup on initial load
        runCookieCleanup()

        // Set up periodic cleanup if interval is specified
        if (intervalMs > 0) {
            const interval = setInterval(runCookieCleanup, intervalMs)
            return () => clearInterval(interval)
        }
    }, [intervalMs])
}
